//is_deleted_from_rml values
// 0 - not deleted - show images
// 1 - original - not show
// 2 - edited deleted - show edited image
// 3 - both deleted - not in RML

import { GetUTCTime } from "../../../Helpers/GetUTCTime";

// Each matched record keeps its own state and gains the bit for the tile being deleted, so deleting the original after the
// decorated copy (or the reverse) lands on 3 instead of overwriting the other half with the clicked tile's state.
const RML_ORIGINAL_DELETED = 1;
const RML_EDITED_DELETED = 2;
// Copies of a decorated image added from the library (line, category) are saved with edited=false, so neither a record's nor the clicked
// tile's flag says which half is being deleted. An image is a decorated copy when any record holding that url is edited=true.
const collectEditedUrls = (inspectionData) => {
  const editedUrls = new Set();
  const pending = [inspectionData?.inspectiontemplates];
  while (pending.length > 0) {
    const node = pending.pop();
    if (Array.isArray(node)) {
      node.forEach((child) => pending.push(child));
    } else if (node && typeof node === "object") {
      if (node.edited === true && typeof node.onlineUrl === "string" && node.onlineUrl) editedUrls.add(node.onlineUrl);
      Object.values(node).forEach((child) => {
        if (child && typeof child === "object") pending.push(child);
      });
    }
  }
  return editedUrls;
};
export const markDeletedHalf = (inspectionData, mediaItem) => {
  if (mediaItem && !mediaItem.edited && collectEditedUrls(inspectionData).has(mediaItem.onlineUrl)) mediaItem.edited = true;
};
const getRmlDeletedState = (currentState, mediaItem) =>
  (Number(currentState) || 0) | (mediaItem?.edited ? RML_EDITED_DELETED : RML_ORIGINAL_DELETED);
// A record is soft-deleted only when it holds the image being deleted; the flag only decides when a record has no url to compare.
export const isRecordDeleted = (record, mediaItem) =>
  record?.onlineUrl && mediaItem?.onlineUrl ? record.onlineUrl === mediaItem.onlineUrl : record?.edited == mediaItem?.edited;

// In the new flow a decorated copy keeps the uuid of its original (and of every other decoration), so uuid alone would hit all of them.
// When both sides have a url the url decides; a decorated tile matches only itself, an original tile also matches copies derived from it.
export const isSameRmlMedia = (record, mediaItem) => {
  if (record?.onlineUrl && mediaItem?.onlineUrl) {
    return record.onlineUrl === mediaItem.onlineUrl || (!mediaItem.edited && record.OriginUrl === mediaItem.onlineUrl);
  }
  return record?.uuid == mediaItem?.uuid || Boolean(mediaItem?.onlineUrl) && mediaItem.onlineUrl == record?.OriginUrl;
};

const DeleteRMLMedia = (mainData, mediaItem) => {
  return new Promise(async (resolve, reject) => {
    try {
      const utcTime = await GetUTCTime();
      var DataMain = JSON.parse(JSON.stringify(mainData?.inspectionDataForBulkMedia?.data));
      mediaItem.updated_at = utcTime;
      markDeletedHalf(DataMain, mediaItem);
      let mediaLibrary = mainData?.inspectionDataForBulkMedia?.data?.report_media_library_files || [];
      const rmlMediaIndex = mediaLibrary.findIndex((i) => {
        return i.uuid == mediaItem.uuid;
      });
      const rmlFilesForDelete = [];
      if (rmlMediaIndex !== -1) {
        if (!mediaLibrary[rmlMediaIndex].created_at || mediaLibrary[rmlMediaIndex].created_at === "Invalid date") {
          mediaLibrary[rmlMediaIndex].created_at = utcTime;
        }
        mediaLibrary[rmlMediaIndex]["is_deleted_from_rml"] = getRmlDeletedState(mediaLibrary[rmlMediaIndex]["is_deleted_from_rml"], mediaItem);
        mediaLibrary[rmlMediaIndex].updated_at = utcTime;
        rmlFilesForDelete.push(mediaLibrary[rmlMediaIndex]);
      }
      let orderTemplates = DataMain?.inspectiontemplates || [];
      orderTemplates = orderTemplates.filter((itm) => {
        return itm?.is_deleted !== 1;
      });
      //pruned templates: only the template/category/line/comment/control actually touched by this delete,
      //so the API payload doesn't have to carry the whole inspection tree
      let prunedTemplates = [];
      for (let templateIndex in orderTemplates) {
        let template = orderTemplates[templateIndex];
        if (template?.is_pdf_template === 1) {
          //pdf template
          let pdfData = template?.pdf_data || [];
          const { pdfData: updatedPDFData, touched, prunedPdfData } = await updatePDFData(pdfData, mediaItem);
          orderTemplates[templateIndex].pdf_data = updatedPDFData;
          if (touched) {
            let prunedTemplate = { ...orderTemplates[templateIndex], pdf_data: prunedPdfData };
            delete prunedTemplate.pdf_categories;
            prunedTemplates.push(prunedTemplate);
          }
        } else if (template?.is_narrative_template === 1) {
          //narrative template
          const { template: updatedTemplate, prunedTemplate } = await updateTemplate(template, mediaItem, 1);
          orderTemplates[templateIndex] = updatedTemplate;
          if (prunedTemplate) {
            prunedTemplates.push(prunedTemplate);
          }
        } else {
          //normal template
          const { template: updatedTemplate, prunedTemplate } = await updateTemplate(template, mediaItem, 0);
          orderTemplates[templateIndex] = updatedTemplate;
          if (prunedTemplate) {
            prunedTemplates.push(prunedTemplate);
          }
        }
      }
      DataMain.inspectiontemplates = orderTemplates;
      resolve({ ...DataMain,rmlFilesForDelete:rmlFilesForDelete, prunedInspectiontemplates: prunedTemplates });
    } catch (err) {
      reject(err);
    }
  });
};
const updatePDFData = async (pdfData, mediaItem) => {
  return new Promise(async (resolve, reject) => {
    try {
      const utcTime = await GetUTCTime();
      //prunedPdfData: only the MediaUploader element(s) that held this media, and only the matched
      //media entry within each, so a single-media delete doesn't resend the whole pdf_data array
      let prunedPdfData = [];
      pdfData?.forEach((element, index) => {
        let pdfElement = element;
        let elementTouched = false;
        let touchedValues = [];
        //check for media in pdf_data
        if (element?.type == "MediaUploader") {
          for (let mediaIndex in element?.value || []) {
            if (isSameRmlMedia(element?.value[mediaIndex], mediaItem)) {
              pdfElement.value[mediaIndex]["is_deleted_from_rml"] = getRmlDeletedState(pdfElement.value[mediaIndex]["is_deleted_from_rml"], mediaItem);
              pdfElement.value[mediaIndex].updated_at = utcTime;
              if (isRecordDeleted(element?.value[mediaIndex], mediaItem)) {
                pdfElement.value[mediaIndex].is_deleted = 1;
              }
              pdfElement["updated_at"] = utcTime;
              elementTouched = true;
              touchedValues.push(pdfElement.value[mediaIndex]);
            }
          }
        }
        pdfData[index] = pdfElement;
        if (elementTouched) {
          prunedPdfData.push({ ...pdfElement, value: touchedValues });
        }
      });
      resolve({ pdfData, touched: prunedPdfData.length > 0, prunedPdfData });
    } catch (err) {
      reject(false);
    }
  });
};
const updateTemplate = async (orderTemplate, mediaItem, isNarrative) => {
  return new Promise(async (resolve, reject) => {
    try {
      let template = orderTemplate;
      const utcTime = await GetUTCTime();
      template["updated_at"] = utcTime;
      let prunedCategories = [];
      (template?.categories || [])?.forEach((category, category_index) => {
        let categoryTouched = false;
        let prunedLines = [];
        (category?.lines || [])?.forEach((line, line_index) => {
          let comments = line?.comments || [];
          let lineInputControls = line?.line_input_controls || [];
          let lineTouched = false;
          let prunedLineInputControls = [];
          let prunedComments = [];
          if (isNarrative == 0) {
            lineInputControls?.forEach((LInput, LIindex) => {
              if (LInput?.type == "Nameplate") {
                let namePlateMedia = LInput?.data?.image || {};
                if (namePlateMedia && isSameRmlMedia(namePlateMedia, mediaItem)) {
                  template.categories[category_index].lines[line_index].line_input_controls[LIindex].data.image["is_deleted_from_rml"] = getRmlDeletedState(template.categories[category_index].lines[line_index].line_input_controls[LIindex].data.image["is_deleted_from_rml"], mediaItem);
                  template.categories[category_index].lines[line_index].line_input_controls[LIindex].data.image.updated_at = utcTime;
                  if (namePlateMedia && isRecordDeleted(namePlateMedia, mediaItem)) {
                    template.categories[category_index].lines[line_index].line_input_controls[LIindex].data.image.is_deleted = 1;
                  }
                  template.categories[category_index]["updated_at"] = utcTime;
                  template.categories[category_index].lines[line_index].line_input_controls[LIindex]["updated_at"] = utcTime;

                  lineTouched = true;
                  categoryTouched = true;
                  prunedLineInputControls.push(template.categories[category_index].lines[line_index].line_input_controls[LIindex]);
                }
              }
              if (LInput?.type == "Media") {
                let lineMedia = LInput?.data || [];
                const touchedLineMedia = [];
                for (let mediaIndex in lineMedia) {
                  if (isSameRmlMedia(lineMedia[mediaIndex], mediaItem)) {
                    template.categories[category_index].lines[line_index].line_input_controls[LIindex].data[mediaIndex]["is_deleted_from_rml"] = getRmlDeletedState(template.categories[category_index].lines[line_index].line_input_controls[LIindex].data[mediaIndex]["is_deleted_from_rml"], mediaItem);
                    template.categories[category_index].lines[line_index].line_input_controls[LIindex].data[mediaIndex].updated_at = utcTime;
                    if (isRecordDeleted(lineMedia[mediaIndex], mediaItem)) {
                      template.categories[category_index].lines[line_index].line_input_controls[LIindex].data[mediaIndex].is_deleted = 1;
                    }
                    template.categories[category_index]["updated_at"] = utcTime;
                    template.categories[category_index].lines[line_index].line_input_controls[LIindex]["updated_at"] = utcTime;

                    lineTouched = true;
                    categoryTouched = true;
                    touchedLineMedia.push(template.categories[category_index].lines[line_index].line_input_controls[LIindex].data[mediaIndex]);
                  }
                }
                if (touchedLineMedia.length > 0) {
                  prunedLineInputControls.push({
                    ...template.categories[category_index].lines[line_index].line_input_controls[LIindex],
                    data: touchedLineMedia,
                  });
                }
              }
            });
          }
          comments?.forEach((comment, comment_index) => {
            let commentTouched = false;
            let prunedCommentInputControls = [];
            (comment?.comment_input_controls || [])?.forEach((CInput, comment_input_index) => {
              if (CInput?.type == "Media") {
                let commentMedia = CInput?.data || [];
                const touchedMedia = [];
                for (let media in commentMedia) {
                  if (isSameRmlMedia(commentMedia[media], mediaItem)) {
                    template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index].data[media]["is_deleted_from_rml"] = getRmlDeletedState(template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index].data[media]["is_deleted_from_rml"], mediaItem);
                    template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index].data[media]["updated_at"] = utcTime;
                    if (isRecordDeleted(commentMedia[media], mediaItem)) {
                      template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index].data[media].is_deleted = 1;
                    }
                    template.categories[category_index]["updated_at"] = utcTime;
                    template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index]["updated_at"] = utcTime;

                    commentTouched = true;
                    lineTouched = true;
                    categoryTouched = true;
                    touchedMedia.push(template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index].data[media]);
                  }
                }
                // One entry per input control: the same RML media can appear several times in one control/comment
                if (touchedMedia.length > 0) {
                  prunedCommentInputControls.push({
                    ...template.categories[category_index].lines[line_index].comments[comment_index].comment_input_controls[comment_input_index],
                    data: touchedMedia,
                  });
                }
              }
            });
            if (commentTouched) {
              prunedComments.push({
                ...template.categories[category_index].lines[line_index].comments[comment_index],
                comment_input_controls: prunedCommentInputControls,
              });
            }
          });
          if (lineTouched) {
            let prunedLine = { ...template.categories[category_index].lines[line_index] };
            delete prunedLine.inspector_flagging_guids;
            if (prunedLineInputControls.length > 0) {
              prunedLine.line_input_controls = prunedLineInputControls;
            } else {
              delete prunedLine.line_input_controls;
            }
            if (prunedComments.length > 0) {
              prunedLine.comments = prunedComments;
            } else {
              delete prunedLine.comments;
            }
            prunedLines.push(prunedLine);
          }
        });
        if (categoryTouched) {
          let prunedCategory = { ...template.categories[category_index], lines: prunedLines };
          delete prunedCategory.inspector_flagging_guids;
          delete prunedCategory.introductory_text_media;
          prunedCategories.push(prunedCategory);
        }
      });
      let prunedTemplate = null;
      if (prunedCategories.length > 0) {
        prunedTemplate = { ...template, categories: prunedCategories };
        delete prunedTemplate.location_data;
        delete prunedTemplate.rating_data;
      }
      resolve({ template, prunedTemplate });
    } catch (err) {
      console.log("err updateTemplate ===>", err);
      reject(false);
    }
  });
};

export default DeleteRMLMedia;